<template>
  <div class="ui-accordion" :class="[{ 'is-active': modelValue, 'ui-accordion--thin-border': thinBorder }, `ui-accordion--${size}`]">
    <div class="accordion-header" @click="toggleAccordion">
      <div class="accordion-title">
        <slot name="titlePrefix"></slot>
        <span class="accordion-title__text">{{ title }}</span>
      </div>
      <div v-if="$slots.headerActions" class="accordion-header-actions" @click.stop>
        <slot name="headerActions"></slot>
      </div>
      <div class="accordion-icon" :class="{ 'accordion-icon--open': modelValue }">
        <Icon name="heroicons:chevron-down" size="16" />
      </div>
    </div>
    <div v-show="modelValue" class="accordion-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
  const props = defineProps({
    title: {
      type: String,
      required: true
    },
    modelValue: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    thinBorder: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['update:modelValue'])

  function toggleAccordion() {
    emit('update:modelValue', !props.modelValue)
  }
</script>

<style lang="scss" scoped>
  .ui-accordion {
    border-radius: 8px;
    border: 2px solid var(--color-gray-20, #cdd1d5);
    background: var(--color-gray-0, #fff);
    margin-bottom: 8px;

    // 활성화 상태 스타일
    &.is-active {
      .accordion-title {
        color: var(--color-primary-50, #00aaff);
      }
      .iconify {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath fill='none' stroke='%2300aaff' stroke-linecap='round' stroke-linejoin='round' stroke-width='10' d='m19.5 8.25l-7.5 7.5l-7.5-7.5'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
      }
      border: 2px solid #9df;
    }

    // 얇은 테두리 스타일 — 활성화 상태에서도 파란 테두리 없이 유지
    &--thin-border,
    &--thin-border.is-active {
      border: 1px solid #e5e7eb;
    }

    // 사이즈별 스타일 정의
    &--small {
      .accordion-header {
        padding: 8px 12px;
      }

      .accordion-title {
        font-size: 14px;
        line-height: 16px;
      }

      .accordion-content {
        padding: 8px 12px;
      }

      .accordion-icon {
        .iconify {
          width: 14px;
          height: 14px;
        }
      }
    }

    &--medium {
      // 기본 스타일 유지
    }

    &--large {
      .accordion-header {
        padding: 16px 20px;
      }

      .accordion-title {
        font-size: 22px;
        line-height: 24px;
      }

      .accordion-content {
        padding: 16px 20px;
      }
    }
  }

  .accordion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    user-select: none;
    border-radius: 8px;
    position: relative;

    // &::before {
    //   content: '';
    //   position: absolute;
    //   left: 12px;
    //   top: 50%;
    //   transform: translateY(-50%);
    //   width: 6px;
    //   height: 6px;
    //   border-radius: 50%;
    //   background: var(--color-primary-50, #6d7882);
    //   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    // }

    // 기본 불릿 스타일 공통 속성
    &.accordion-title-with-bullet {
      position: relative;
      padding-left: 28px; // 여유 공간 조금 더 확보

      // 1. 기본 원형 불릿 (그라데이션 효과 추가)
      &.bullet-circle::before {
        content: '';
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--color-primary-50, #6d7882);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
    }

    // 아코디언 활성화 상태에 따른 불릿 스타일 변화
    &.active {
      &.accordion-title-with-bullet {
        &.bullet-circle::before {
          background: linear-gradient(135deg, #34e89e, #0f3443);
        }

        &.bullet-arrow::before {
          content: '⌄';
          transform: translateY(-50%) rotate(0deg);
          transition: transform 0.3s ease;
        }
      }
    }
  }

  .accordion-header:hover {
    background-color: var(--color-gray-10, #f1f1f1);
  }

  .accordion-header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    margin-left: auto;
    margin-right: 8px;
  }

  .accordion-title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    color: #6d7882;
    font-size: var(--font_size-font_S-title, 20px);
    font-weight: 700;
    line-height: 20px;

    &__text {
      min-width: 0;
    }
  }

  .accordion-icon {
    transition: transform 0.3s ease;
  }

  .accordion-icon--open {
    transform: rotate(180deg);
  }

  .accordion-content {
    padding: 12px 16px;
    background-color: var(--color-gray-0, #fff);
    border-radius: 8px;
    min-width: 0;
    max-width: 100%;
  }

  .ui-accordion.accordion-gray-ver{
    margin-bottom: 16px;
    border-radius: 6px;
    border: 1px solid var(--color-gray-20, #CDD1D5);
    background: var(--color-gray-wt, #FFF);

    &.is-active{
      .accordion-header{
        padding-bottom: 10px;

        &::after{
          content: '';
          position: absolute;
          left: 50%;
          top: auto;
          transform: translateX(-50%);
          bottom: 0;
          width: calc(100% - 32px);
          height: 1px;
          background-color: #8A949E;
        }
      }
    }

    .accordion-header{
      position: relative;
      padding: 12px 16px;
      min-height: 48px;
      &:hover{
        background-color: #fff;
      }
      .accordion-title{
        font-size: 18px;
        font-weight: 700;
        color: var(--color-gray-70, #464C53);
      }

      .accordion-icon{
        width: 20px;
        height: 20px;
        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.6128 6.17114C16.8865 6.41744 16.9087 6.83896 16.6624 7.11264L10.6626 13.7793C10.5362 13.9198 10.3561 14 10.1671 14C9.97811 14 9.798 13.9198 9.67157 13.7793L3.67116 7.11267C3.42484 6.839 3.44702 6.41748 3.72068 6.17116C3.99435 5.92484 4.41587 5.94702 4.66219 6.22068L10.1671 12.3368L15.6713 6.22071C15.9176 5.94703 16.3391 5.92484 16.6128 6.17114Z' fill='%23464C53'/%3E%3C/svg%3E%0A");
        background-repeat: no-repeat;
        background-position: center center;
        background-size: 20px;

        span{
          display: none;
        }
      }
    }
  }
</style>
